import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import {
    ClientDto,
    DemandOrderDto,
    GoodsDto,
    GoodsStatus,
    OrderDto,
    OrderGoodsItemDto,
    OrderStatus,
    ReturnOrderDto,
    SiteDto,
    SpecializedOrderStatus,
} from '../../api/ClientApi';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    DialogActions,
    DialogContent,
    IconButton,
    Paper,
    TablePagination,
    Tooltip,
    Typography,
} from '@mui/material';
import { StyledTableCell } from '../../common/components/styled/StyledTable';
import { CompleteOrderType, OrderStatusSR, OrderTypeSR, SpecializedOrderStatusSR } from '../../common/Types';
import {
    getClientNameInfo,
    getColorForOrderStatus,
    getColorForOrderType,
    getSiteOrdersTableData,
} from './OrdersHelper';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/reducers';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { useSitesWithGoods } from '../../hooks/useSitesWithGoods';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import OrderForm from './forms/OrderForm';
import ReceiptForm from './forms/ReceiptForm';
import { convertGoodsIdsToBareGoodsEntities, convertOrderGoodsItemsToBareGoodsEntities } from './forms/OrderFormHelper';
import { filterUniqueBareGoodsByArticle } from '../../redux/actions/helper';
import ErrorIcon from '@mui/icons-material/Error';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ReceiptsList from './ReceiptsList';
import WaybillForm from './forms/WaybillForm';
import { importApprovedOrderGoodsItems } from '../../redux/actions/orderGoodsItemsActions';
import OrdersTableMenu from './OrdersTableMenu';
import CancelOrderForm from './forms/CancelOrderForm';
import {
    toggleDemandOrderIdCollapsible,
    toggleGeneralReturnOrderIdCollapsible,
    toggleOrderIdCollapsible,
} from '../../redux/actions/collapsibleOpenStateActions';
import { isCompleteOrderCollapsed } from '../../redux/selectors/collapsibleSelector';
import { CompareTwoDateTimes, FormatDateText } from '../../common/datesHelper';
import DemandOrderForm from './forms/DemandOrderForm';
import ReturnOrderForm from './forms/ReturnOrderForm';
import { getColorForSpecializedOrderStatus } from '../purchaseOrders/PurchaseOrdersHelper';
import CloseOrderForm from './forms/CloseOrderForm';
import BoltIcon from '@mui/icons-material/Bolt';
import { rejectOrderDelivery } from '../../redux/actions/orderActions';
import {
    isEmployeeProjectManager,
    isEmployeeWarehouseOwner,
    isEmployeeWarehouseOwnerOnly,
    isEmployeeWarehouseManager,
} from '../../redux/selectors/employeeSelector';
import { useLocation, useNavigate } from 'react-router-dom';
import AcceptOrderForm from './forms/AcceptOrderForm';

import { FormDialogTitle } from '../../common/components/form/FormPrimitives';

import {
    ResponsiveDialog,
    MobileCardRow,
    mobileHiddenHeadSx,
    useIsMobile,
} from '../../common/components/mobile/MobilePrimitives';

type ArticleQuantityType = {
    article: string;
    quantity: number;
    alreadyOrderedQuantity?: number;
};

function createData(
    id?: number,
    orderType?: CompleteOrderType,
    originSite?: SiteDto,
    supplier?: ClientDto,
    destinationSite?: SiteDto,
    orderStatus?: OrderStatus,
    specializedOrderStatus?: SpecializedOrderStatus,
    orderGoodsItems?: OrderGoodsItemDto[],
    sites?: SiteDto[] | null,
    clients?: ClientDto[] | null,
    isThereEnoughGoodsForOrder?: boolean,
    areThereReceipts?: boolean,
    created?: Date,
    alreadyOrderedItems?: OrderGoodsItemDto[],
    originSiteOwners?: string[],
    originSiteProjectManagers?: string[],
    destinationSiteOwners?: string[],
    waybillId?: number,
    goods?: GoodsDto[],
    isExpress?: boolean,
) {
    const destinationData = !destinationSite
        ? '/'
        : getSiteOrdersTableData(destinationSite, sites ?? undefined, clients ?? undefined);

    const originData = !originSite
        ? !supplier
            ? '/'
            : getClientNameInfo(supplier)
        : getSiteOrdersTableData(originSite, sites ?? undefined, clients ?? undefined);

    const art =
        !!orderGoodsItems && orderGoodsItems.length > 0
            ? orderGoodsItems
                  ?.filter((g) => !!g.article?.title && !!g.quantity)
                  .map((g) => {
                      return { article: g.article, quantity: g.quantity ?? 0 };
                  })
            : !!goods
              ? goods
                    ?.filter((g) => !!g.article?.title && !!g.quantity)
                    .map((g) => {
                        return { article: g.article, quantity: g.quantity ?? 0 };
                    })
              : [];

    const artUnique: ArticleQuantityType[] = [];

    art.map((a) => {
        const uniqueKey =
            (a.article?.title ?? '') +
            ', ' +
            (a.article?.manufacturer ?? '') +
            ' (' +
            (a.article?.innerCode ?? '') +
            ')';
        const oldArtIndex = artUnique.findIndex((au) => au.article === uniqueKey);
        if (oldArtIndex === undefined || oldArtIndex === -1) {
            const alreadyOrderedQuantity =
                orderType === CompleteOrderType.Demand ||
                orderType === CompleteOrderType.GeneralReturn ||
                orderStatus === OrderStatus.Delivered
                    ? (alreadyOrderedItems
                          ?.filter((aoi) => aoi.articleId === a.article?.id)
                          .reduce((acc, val) => {
                              return (acc += val.quantity ?? 0);
                          }, 0) ?? undefined)
                    : undefined;

            const aCopy: ArticleQuantityType = {
                article: uniqueKey,
                quantity: a.quantity,
                alreadyOrderedQuantity: alreadyOrderedQuantity,
            };

            artUnique.push(aCopy);
        } else {
            artUnique[oldArtIndex].quantity += a.quantity;
        }
    });

    return {
        id,
        orderType,
        originData,
        destinationData,
        orderStatus,
        specializedOrderStatus,
        artUnique,
        isThereEnoughGoodsForOrder,
        areThereReceipts,
        created,
        originSiteOwners,
        originSiteProjectManagers,
        destinationSiteOwners,
        waybillId,
        isExpress: !!isExpress,
    };
}

type OrderRow = ReturnType<typeof createData>;

type OrderRowActions = {
    goToOrderForm: (orderId?: number, orderType?: CompleteOrderType) => void;
    goToAcceptOrderForm: (orderId?: number, orderType?: CompleteOrderType) => void;
    goToReceiptForm: (orderId?: number, orderType?: CompleteOrderType) => void;
    goToWaybillForm: (orderId?: number, orderType?: CompleteOrderType) => void;
    goToCancelOrderForm: (orderId?: number, orderType?: CompleteOrderType) => void;
    goToCloseOrderForm: (orderId?: number, orderType?: CompleteOrderType) => void;
    goToDemandOrderForm: (orderId?: number, orderType?: CompleteOrderType) => void;
    goToReturnOrderForm: (orderId?: number, orderType?: CompleteOrderType) => void;
    acceptDemandOrder: (orderId?: number, orderType?: CompleteOrderType) => void;
    acceptReturnOrder: (orderId?: number, orderType?: CompleteOrderType) => void;
    goToPrintWaybill: (waybillId: number) => void;
    rejectDelivery: (orderId?: number) => void;
    goToReceiptListForOrder: (orderId?: number, orderType?: CompleteOrderType) => void;
    goToOrderDetails: (orderId?: number, orderType?: CompleteOrderType) => void;
    allowGoingToOrderDetails: (orderType?: CompleteOrderType) => boolean;
};

type OrderRowProps = {
    row: OrderRow;
    disableActions?: boolean;
    empId?: string;
    isWarehouseOwner: boolean;
    isProjectManager: boolean;
    isWarehouseManager: boolean;
    actions: OrderRowActions;
};

/**
 * One order row: desktop table row + collapsible details, or a mobile card.
 * Lives at module scope so React updates rows in place instead of remounting them on every parent render.
 */
function Row({
    row,
    disableActions,
    empId,
    isWarehouseOwner,
    isProjectManager,
    isWarehouseManager,
    actions,
}: OrderRowProps) {
    const dispatch = useAppDispatch();
    const isCollapsed = useAppSelector(isCompleteOrderCollapsed(row.id, row.orderType));

    const handleToggleOrderCollapsible = () => {
        row.orderType === CompleteOrderType.Demand
            ? dispatch(toggleDemandOrderIdCollapsible(row.id))
            : row.orderType === CompleteOrderType.GeneralReturn
              ? dispatch(toggleGeneralReturnOrderIdCollapsible(row.id))
              : dispatch(toggleOrderIdCollapsible(row.id));
    };

    const isEmployeeOriginSiteOwner = !!empId ? (row.originSiteOwners ?? []).includes(empId) : false;

    const isMobile = useIsMobile();

    const rowMenu = !disableActions ? (
        <OrdersTableMenu
            goToOrderForm={() => actions.goToOrderForm(row.id, row.orderType)}
            goToAcceptOrderForm={() => actions.goToAcceptOrderForm(row.id, row.orderType)}
            goToReceiptForm={() => actions.goToReceiptForm(row.id, row.orderType)}
            goToWaybillForm={() => actions.goToWaybillForm(row.id, row.orderType)}
            goToCancelOrderForm={() => actions.goToCancelOrderForm(row.id, row.orderType)}
            acceptDemandOrder={() => actions.acceptDemandOrder(row.id, row.orderType)}
            acceptReturnOrder={() => actions.acceptReturnOrder(row.id, row.orderType)}
            goToCloseOrderForm={() => actions.goToCloseOrderForm(row.id, row.orderType)}
            menuOrderStatus={row.orderStatus}
            menuSpecializedOrderStatus={row.specializedOrderStatus}
            goToDemandOrderForm={() => actions.goToDemandOrderForm(row.id, row.orderType)}
            goToReturnOrderForm={() => actions.goToReturnOrderForm(row.id, row.orderType)}
            goToPrintWaybill={() => actions.goToPrintWaybill(row.waybillId ?? 0)}
            rejectDelivery={() => actions.rejectDelivery(row.id)}
            orderType={row.orderType}
            originSiteOwners={row.originSiteOwners}
            originSiteProjectManagers={row.originSiteProjectManagers}
            destinationSiteOwners={row.destinationSiteOwners}
        />
    ) : null;

    if (isMobile) {
        const isEmployeeOriginSiteProjectManager = !!empId && (row.originSiteProjectManagers ?? []).includes(empId);
        const isEmployeeDestinationSiteOwner = !!empId && (row.destinationSiteOwners ?? []).includes(empId);
        const isOpenSpecialized =
            row.specializedOrderStatus === SpecializedOrderStatus.Created ||
            row.specializedOrderStatus === SpecializedOrderStatus.InProgress;

        // Jedno dugme za sledeći korak, ista pravila kao u OrdersTableMenu.
        const primaryAction: {
            label: string;
            color: 'primary' | 'success' | 'warning';
            onClick: () => void;
        } | null = disableActions
            ? null
            : row.orderType === CompleteOrderType.Demand &&
                (isProjectManager || isWarehouseManager) &&
                isOpenSpecialized
              ? {
                    label: 'Kreiraj nalog',
                    color: 'success',
                    onClick: () => actions.acceptDemandOrder(row.id, row.orderType),
                }
              : row.orderType === CompleteOrderType.GeneralReturn &&
                  (isProjectManager || isWarehouseManager) &&
                  isOpenSpecialized
                ? {
                      label: 'Kreiraj nalog',
                      color: 'success',
                      onClick: () => actions.acceptReturnOrder(row.id, row.orderType),
                  }
                : row.orderStatus === OrderStatus.Idle && isProjectManager && isEmployeeOriginSiteProjectManager
                  ? {
                        label: 'Prihvati nalog',
                        color: 'success',
                        onClick: () => actions.goToAcceptOrderForm(row.id, row.orderType),
                    }
                  : row.orderStatus === OrderStatus.Pending && isWarehouseOwner && isEmployeeOriginSiteOwner
                    ? {
                          label: 'Prihvati nalog',
                          color: 'success',
                          onClick: () => actions.goToReceiptForm(row.id, row.orderType),
                      }
                    : row.orderStatus === OrderStatus.Approved && isWarehouseOwner && isEmployeeOriginSiteOwner
                      ? {
                            label: 'Pošalji nalog',
                            color: 'primary',
                            onClick: () => actions.goToWaybillForm(row.id, row.orderType),
                        }
                      : row.orderStatus === OrderStatus.Transport && isWarehouseOwner && isEmployeeDestinationSiteOwner
                        ? {
                              label: 'Preuzmi robu',
                              color: 'warning',
                              onClick: () => actions.goToReceiptForm(row.id, row.orderType),
                          }
                        : null;

        const showsOrderedQuantity =
            row.orderType === CompleteOrderType.Demand ||
            row.orderType === CompleteOrderType.GeneralReturn ||
            row.orderStatus === OrderStatus.Delivered;

        return (
            <MobileCardRow
                onClick={
                    actions.allowGoingToOrderDetails(row.orderType)
                        ? () => actions.goToOrderDetails(row.id, row.orderType)
                        : undefined
                }
                highlighted={row.orderStatus === OrderStatus.Transport && isWarehouseOwner && isEmployeeOriginSiteOwner}
                header={
                    <>
                        {row.orderType && (
                            <Chip
                                size="small"
                                variant="outlined"
                                label={OrderTypeSR[row.orderType]}
                                color={getColorForOrderType(row.orderType)}
                            />
                        )}
                        {row.orderStatus && (
                            <Chip
                                size="small"
                                variant="outlined"
                                label={OrderStatusSR[row.orderStatus]}
                                color={getColorForOrderStatus(row.orderStatus)}
                            />
                        )}
                        {row.specializedOrderStatus && (
                            <Chip
                                size="small"
                                variant="outlined"
                                label={SpecializedOrderStatusSR[row.specializedOrderStatus]}
                                color={getColorForSpecializedOrderStatus(row.specializedOrderStatus)}
                            />
                        )}
                        {row.isExpress && (
                            <Chip size="small" variant="outlined" color="warning" icon={<BoltIcon />} label="Ubrzano" />
                        )}
                        {!row.isThereEnoughGoodsForOrder && (
                            <Tooltip title="Nedovoljno robe za izvršenje naloga">
                                <ErrorIcon sx={{ color: 'red' }} fontSize="small" />
                            </Tooltip>
                        )}
                    </>
                }
                actions={
                    <Box sx={{ display: 'flex' }}>
                        {row.areThereReceipts && (
                            <IconButton
                                color="warning"
                                size="small"
                                onClick={() => actions.goToReceiptListForOrder(row.id)}
                            >
                                <ReceiptLongIcon />
                            </IconButton>
                        )}
                        {rowMenu}
                    </Box>
                }
                fields={[
                    { label: 'Odakle', value: row.originData ?? '/' },
                    { label: 'Kuda', value: row.destinationData ?? '/' },
                    { label: 'Kreiran', value: row.created ? FormatDateText(row.created) : '/' },
                    { label: 'Br.', value: row.id ?? '/' },
                ]}
            >
                {row.artUnique.length > 0 && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', columnGap: 1, rowGap: 0.25 }}>
                        {row.artUnique.map((article) => (
                            <React.Fragment key={article.article}>
                                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                    {article.article ?? '/'}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                                    {showsOrderedQuantity && (
                                        <span style={{ opacity: 0.5 }}>
                                            {(article.alreadyOrderedQuantity ?? 0).toString()}/
                                        </span>
                                    )}
                                    {(article.quantity ?? '/').toString()}
                                </Typography>
                            </React.Fragment>
                        ))}
                    </Box>
                )}
                {primaryAction && (
                    <Button
                        fullWidth
                        variant="contained"
                        color={primaryAction.color}
                        sx={{ mt: 1.5, textTransform: 'none' }}
                        onClick={(e) => {
                            e.stopPropagation();
                            primaryAction.onClick();
                        }}
                    >
                        {primaryAction.label}
                    </Button>
                )}
            </MobileCardRow>
        );
    }

    return (
        <>
            <TableRow
                sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    bgcolor: !isCollapsed
                        ? '#eee'
                        : row.orderStatus === OrderStatus.Transport && isWarehouseOwner && isEmployeeOriginSiteOwner
                          ? '#f5f5f5'
                          : '',
                }}
            >
                <TableCell
                    sx={{
                        display: { xs: 'none', sm: 'table-cell' },
                    }}
                >
                    <IconButton aria-label="expand row" size="small" onClick={() => handleToggleOrderCollapsible()}>
                        {!isCollapsed ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell
                    component="th"
                    scope="row"
                    sx={{
                        display: { xs: 'none', sm: 'table-cell' },
                    }}
                >
                    {row.id ?? '/'}
                </TableCell>
                <TableCell
                    align="center"
                    onClick={() => actions.goToOrderDetails(row.id, row.orderType)}
                    sx={{
                        px: { xs: 0, sm: 2 },
                    }}
                >
                    {row.orderType ? (
                        <Chip
                            variant="outlined"
                            label={OrderTypeSR[row.orderType]}
                            color={getColorForOrderType(row.orderType)}
                            sx={{
                                cursor: actions.allowGoingToOrderDetails(row.orderType) ? 'pointer' : 'auto',
                                width: { xs: 100, sm: 'inherit' },
                            }}
                        />
                    ) : (
                        '/'
                    )}
                </TableCell>
                <TableCell
                    align="center"
                    sx={{
                        display: { xs: 'none', sm: 'table-cell' },
                    }}
                >
                    <IconButton
                        color="warning"
                        onClick={() => actions.goToReceiptListForOrder(row.id)}
                        sx={{ m: 0 }}
                        disabled={!row.areThereReceipts}
                    >
                        <ReceiptLongIcon />
                    </IconButton>
                </TableCell>
                <TableCell
                    align="center"
                    sx={{
                        px: { xs: 0, sm: 2 },
                    }}
                >
                    {row.destinationData ?? '/'}
                </TableCell>
                <TableCell
                    align="center"
                    sx={{
                        px: { xs: 0, sm: 2 },
                    }}
                >
                    {row.originData ?? '/'}
                </TableCell>
                <TableCell
                    align="center"
                    sx={{
                        px: { xs: 0, sm: 2 },
                    }}
                >
                    {row.orderStatus ? (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Chip
                                variant="outlined"
                                label={OrderStatusSR[row.orderStatus]}
                                color={getColorForOrderStatus(row.orderStatus)}
                            />
                            {row.isExpress && (
                                <Tooltip title="Ubrzani prenos: roba je bila prebačena pre unosa naloga">
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        color="warning"
                                        icon={<BoltIcon />}
                                        label="Ubrzano"
                                    />
                                </Tooltip>
                            )}
                        </Box>
                    ) : row.specializedOrderStatus ? (
                        <Chip
                            variant="outlined"
                            label={SpecializedOrderStatusSR[row.specializedOrderStatus]}
                            color={getColorForSpecializedOrderStatus(row.specializedOrderStatus)}
                        />
                    ) : (
                        '/'
                    )}
                    {!row.isThereEnoughGoodsForOrder ? (
                        <Box sx={{ display: 'inline-block' }}>
                            <Tooltip title="Nedovoljno robe za izvršenje naloga">
                                <ErrorIcon sx={{ color: 'red' }} />
                            </Tooltip>
                        </Box>
                    ) : (
                        <></>
                    )}
                </TableCell>
                <TableCell
                    align="center"
                    sx={{
                        px: { xs: 0, sm: 2 },
                    }}
                >
                    {row.created ? FormatDateText(row.created) : '/'}
                </TableCell>
                {!disableActions && (
                    <TableCell align="center" sx={{ width: 50, py: 0 }}>
                        <div>{rowMenu}</div>
                    </TableCell>
                )}
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                            <Table size="small" aria-label="purchases">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Artikal</TableCell>
                                        <TableCell align="center">Količina</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {row.artUnique?.map((article) => {
                                        return (
                                            <TableRow
                                                key={article.article}
                                                sx={{
                                                    '&:last-child td, &:last-child th': { border: 0 },
                                                }}
                                            >
                                                <TableCell component="th" scope="row">
                                                    {article.article ?? '/'}
                                                </TableCell>
                                                <TableCell align="center">
                                                    {(row.orderType === CompleteOrderType.Demand ||
                                                        row.orderType === CompleteOrderType.GeneralReturn ||
                                                        row.orderStatus === OrderStatus.Delivered) && (
                                                        <span style={{ opacity: 0.5 }}>
                                                            {(article.alreadyOrderedQuantity ?? 0).toString()}/
                                                        </span>
                                                    )}
                                                    {(article.quantity ?? '/').toString()}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

type OrdersTableProps = {
    orders: OrderDto[] | null;
    returnOrders: ReturnOrderDto[] | null;
    demandOrders: DemandOrderDto[] | null;
    disableActions?: boolean;
};

export default function OrdersTable(props: OrdersTableProps) {
    const { orders, returnOrders, demandOrders, disableActions } = props;

    const isWarehouseOwner = useAppSelector(isEmployeeWarehouseOwner);
    const isProjectManager = useAppSelector(isEmployeeProjectManager);
    const isWarehouseManager = useAppSelector(isEmployeeWarehouseManager);
    const isHighestPrivilageWarehouseOwner = useAppSelector(isEmployeeWarehouseOwnerOnly);

    const empId = useAppSelector((state) => state.loggedEmployee.basicEmployee?.id);

    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const [orderId, setOrderId] = React.useState<number | undefined>(undefined);
    const [acceptOrderId, setAcceptOrderId] = React.useState<number | undefined>(undefined);
    const [receiptFormOrderId, setReceiptFormOrderId] = React.useState<number | undefined>(undefined);
    const [waybillFormOrderId, setWaybillFormOrderId] = React.useState<number | undefined>(undefined);
    const [receiptListOrderId, setReceiptListOrderId] = React.useState<number | undefined>(undefined);
    const [cancelOrderOrderId, setCancelOrderOrderId] = React.useState<number | undefined>(undefined);
    const [closeOrderOrderId, setCloseOrderOrderId] = React.useState<number | undefined>(undefined);
    const [acceptDemandOrderOrderId, setAcceptDemandOrderOrderId] = React.useState<number | undefined>(undefined);
    const [acceptReturnOrderOrderId, setAcceptReturnOrderOrderId] = React.useState<number | undefined>(undefined);
    const [demandOrderId, setDemandOrderId] = React.useState<number | undefined>(undefined);
    const [returnOrderId, setReturnOrderId] = React.useState<number | undefined>(undefined);
    const [orderType, setOrderType] = React.useState<CompleteOrderType | undefined>(undefined);
    const [rejectDeliveryOrderId, setRejectDeliveryOrderId] = React.useState<number | undefined>(undefined);
    const [rejectingDelivery, setRejectingDelivery] = React.useState<boolean>(false);

    const handleConfirmRejectDelivery = async () => {
        if (!rejectDeliveryOrderId) {
            return;
        }
        setRejectingDelivery(true);
        try {
            await dispatch(rejectOrderDelivery(rejectDeliveryOrderId)).unwrap();
        } finally {
            setRejectingDelivery(false);
            setRejectDeliveryOrderId(undefined);
        }
    };

    const goToOrderForm = (orderId?: number, orderType?: CompleteOrderType) => {
        setOrderId(orderId);
        setOrderType(orderType);
    };

    const goToAcceptOrderForm = (orderId?: number, orderType?: CompleteOrderType) => {
        setAcceptOrderId(orderId);
        setOrderType(orderType);
    };

    const handleCloseOrderForm = () => {
        setOrderId(undefined);
        setAcceptDemandOrderOrderId(undefined);
        setAcceptReturnOrderOrderId(undefined);
        setOrderType(undefined);
    };

    const handleCloseAcceptOrderForm = () => {
        setAcceptOrderId(undefined);
        setOrderId(undefined);
        setAcceptDemandOrderOrderId(undefined);
        setAcceptReturnOrderOrderId(undefined);
        setOrderType(undefined);
    };

    const goToDemandOrderForm = (orderId?: number, orderType?: CompleteOrderType) => {
        setDemandOrderId(orderId);
        setOrderType(orderType);
    };

    const handleCloseDemandOrderForm = () => {
        setDemandOrderId(undefined);
        setOrderType(undefined);
    };

    const goToReturnOrderForm = (orderId?: number, orderType?: CompleteOrderType) => {
        setReturnOrderId(orderId);
        setOrderType(orderType);
    };

    const handleCloseReturnOrderForm = () => {
        setReturnOrderId(undefined);
        setOrderType(undefined);
    };

    const goToReceiptForm = (orderId?: number, orderType?: CompleteOrderType) => {
        setReceiptFormOrderId(orderId);
        setOrderType(orderType);
    };

    const goToPrintWaybill = (waybillId: number) => {
        if (waybillId === 0) {
            alert('Došlo je do greške. Otpremnica nije pronađena');
        } else {
            navigate('../waybillPrint/' + waybillId);
        }
    };

    const handleCloseReceiptForm = () => {
        setReceiptFormOrderId(undefined);
        setOrderType(undefined);
    };

    const goToWaybillForm = (orderId?: number, orderType?: CompleteOrderType) => {
        setWaybillFormOrderId(orderId);
        setOrderType(orderType);
    };

    const handleCloseWaybillForm = () => {
        setWaybillFormOrderId(undefined);
        setOrderType(undefined);
    };

    const goToReceiptListForOrder = (oId?: number, orderType?: CompleteOrderType) => {
        setReceiptListOrderId(oId);
        setOrderType(orderType);
    };

    const handleCloseReceiptList = () => {
        setReceiptListOrderId(undefined);
        setOrderType(undefined);
    };

    const goToCancelOrderForm = (orderId?: number, orderType?: CompleteOrderType) => {
        setCancelOrderOrderId(orderId);
        setOrderType(orderType);
    };

    const handleCloseCancelOrderForm = () => {
        setCancelOrderOrderId(undefined);
        setOrderType(undefined);
    };

    const goToCloseOrderForm = (orderId?: number, orderType?: CompleteOrderType) => {
        setCloseOrderOrderId(orderId);
        setOrderType(orderType);
    };

    const handleCloseCloseOrderForm = () => {
        setCloseOrderOrderId(undefined);
        setOrderType(undefined);
    };

    const handleAcceptDemandOrder = (orderId?: number, orderType?: CompleteOrderType) => {
        setAcceptDemandOrderOrderId(orderId);
        setOrderType(orderType);
    };

    // const handleCloseAcceptDemandOrder = () => {
    //     setAcceptDemandOrderOrderId(undefined);
    //     setOrderType(undefined);
    // };

    const handleAcceptReturnOrder = (orderId?: number, orderType?: CompleteOrderType) => {
        setAcceptReturnOrderOrderId(orderId);
        setOrderType(orderType);
    };

    // const handleCloseAcceptReturnOrder = () => {
    //     setAcceptReturnOrderOrderId(undefined);
    //     setOrderType(undefined);
    // };

    const goods = useSelector((state: RootState) => state.goods.goods);
    const approvedOrderGoodsItems = useSelector((state: RootState) => state.orderGoodsItems.approvedOrderGoodsItems);
    const sites = useSitesWithGoods();
    const clients = useSelector((state: RootState) => state.clients.clients);

    // The table renders as soon as the orders arrive; until the goods list is in, availability is
    // unknown and no "not enough stock" warning is shown.
    const goodsLoaded = goods !== null;

    const dispatch = useAppDispatch();

    React.useEffect(() => {
        if (!approvedOrderGoodsItems) {
            dispatch(importApprovedOrderGoodsItems());
        }
    }, [dispatch]);

    const path = useLocation().pathname;

    const orderRows = React.useMemo(() => {
        return orders
            ?.map((order) => {
                if (!isWarehouseOwner && !isProjectManager) {
                    if (
                        (!!order.purchaseOrderId && order.purchaseOrderId > 0) ||
                        (!!order.demandOrderId && order.demandOrderId > 0 && path.indexOf('/demandOrder') === -1) ||
                        (!!order.returnOrderId && order.returnOrderId > 0 && path.indexOf('/returnOrder') === -1)
                    ) {
                        return [];
                    }
                }

                const site = sites?.find((s) => s.id === order.originSiteId);

                const existingBareGoods = convertOrderGoodsItemsToBareGoodsEntities(order.orderGoodsItems, site);
                const filteredGoods = filterUniqueBareGoodsByArticle(existingBareGoods);

                const goodsForSite = goods?.filter((g) => g.siteId === order.originSiteId);
                const unreservedGoodsForSite = goodsForSite?.filter((g) => g.goodsStatus === GoodsStatus.InStock);

                const filteredOrderGoodsItems = approvedOrderGoodsItems?.filter(
                    (ogi) => ogi.order?.originSiteId === site?.id,
                );

                const filteredGoodsForSite = filterUniqueBareGoodsByArticle(
                    convertGoodsIdsToBareGoodsEntities(
                        unreservedGoodsForSite?.map((gfs) => gfs.id ?? 0) ?? [],
                        unreservedGoodsForSite ?? [],
                        filteredOrderGoodsItems ?? [],
                        site,
                    ),
                );

                const isThereEnoughGoodsForOrder = !goodsLoaded
                    ? true
                    : order.orderStatus === OrderStatus.Idle || order.orderStatus === OrderStatus.Pending
                      ? filteredGoods.filter((g) => {
                            if (!g.article?.id || !g.orderedQuantity) {
                                return false;
                            }

                            const myReservedGoods =
                                order.orderStatus !== OrderStatus.Approved
                                    ? 0
                                    : (order?.orderGoodsItems
                                          ?.filter((ogi) => ogi.articleId === g.article?.id)
                                          .reduce((acc, val) => {
                                              return (acc += val.quantity ?? 0);
                                          }, 0) ?? 0);

                            const existingGoods = filteredGoodsForSite.find((fg) => fg.article?.id === g.article?.id);

                            if (!existingGoods) {
                                return true;
                            }

                            if (existingGoods.availableQuantity < g.orderedQuantity - myReservedGoods) {
                                return true;
                            }

                            return false;
                        }).length === 0
                      : true;

                const areThereReceipts = !!order.receipts && order.receipts.length > 0;

                const cOrderType = !!order.supplierId
                    ? CompleteOrderType.Reception
                    : !!order.originSiteId && !!order.destinationSiteId
                      ? CompleteOrderType.Transfer
                      : CompleteOrderType.Reception;

                const deliveredItems =
                    order.orderStatus === OrderStatus.Delivered
                        ? convertGoodsIdsToBareGoodsEntities(
                              order.goodsIds ?? [],
                              order.goods?.filter((g) => g.goodsStatus === GoodsStatus.InStock) ?? [],
                              [],
                          ).map((bge) => {
                              return new OrderGoodsItemDto({
                                  articleId: bge.article?.id,
                                  quantity: bge.orderedQuantity,
                              });
                          })
                        : undefined;

                const originSiteOwners = order.originSite?.warehouseOwnersAzObjIds ?? [];
                const destinationSiteOwners = order.destinationSite?.warehouseOwnersAzObjIds ?? [];

                const originSiteProjectManagers = order.originSite?.project?.projectManagersAzObjIds ?? [];

                return createData(
                    order.id,
                    cOrderType,
                    order.originSite,
                    order.supplier,
                    order.destinationSite,
                    order.orderStatus,
                    undefined,
                    order.orderGoodsItems,
                    sites,
                    clients,
                    isThereEnoughGoodsForOrder,
                    areThereReceipts,
                    order.created,
                    deliveredItems,
                    originSiteOwners,
                    originSiteProjectManagers,
                    destinationSiteOwners,
                    order.waybill?.id,
                    order.goods,
                    order.isExpress,
                );
            })
            .flat();
    }, [orders, goods, sites, clients, approvedOrderGoodsItems, path, isWarehouseOwner, isProjectManager]);

    const rows = React.useMemo(() => {
        const returnOrderRows = returnOrders?.map((order) => {
            const site = sites?.find((s) => s.id === order.originSiteId);

            const existingBareGoods = convertOrderGoodsItemsToBareGoodsEntities(order.orderGoodsItems, site);
            const filteredGoods = filterUniqueBareGoodsByArticle(existingBareGoods);

            const goodsForSite = goods?.filter((g) => g.siteId === order.originSiteId);
            const unreservedGoodsForSite = goodsForSite?.filter((g) => g.goodsStatus === GoodsStatus.InStock);

            const filteredOrderGoodsItems = approvedOrderGoodsItems?.filter(
                (ogi) => ogi.order?.originSiteId === site?.id,
            );

            const filteredGoodsForSite = filterUniqueBareGoodsByArticle(
                convertGoodsIdsToBareGoodsEntities(
                    unreservedGoodsForSite?.map((gfs) => gfs.id ?? 0) ?? [],
                    unreservedGoodsForSite ?? [],
                    filteredOrderGoodsItems ?? [],
                    site,
                ),
            );

            const isThereEnoughGoodsForOrder = !goodsLoaded
                ? true
                : order.returnOrderStatus === SpecializedOrderStatus.Created
                  ? filteredGoods.filter((g) => {
                        if (!g.article?.id || !g.orderedQuantity) {
                            return false;
                        }

                        const existingGoods = filteredGoodsForSite.find((fg) => fg.article?.id === g.article?.id);

                        if (!existingGoods) {
                            return true;
                        }

                        if (existingGoods.availableQuantity < g.orderedQuantity) {
                            return true;
                        }

                        return false;
                    }).length === 0
                  : order.returnOrderStatus === SpecializedOrderStatus.InProgress
                    ? filteredGoods.filter((g) => {
                          if (!g.article?.id || !g.orderedQuantity) {
                              return false;
                          }

                          const existingGoods = filteredGoodsForSite.find((fg) => fg.article?.id === g.article?.id);

                          if (!existingGoods) {
                              return true;
                          }

                          const finishedOrders = order.orders?.filter(
                              (o) =>
                                  o.orderStatus === OrderStatus.Pending ||
                                  o.orderStatus == OrderStatus.Transport ||
                                  o.orderStatus === OrderStatus.Delivered,
                          );

                          const deliveredGoods = finishedOrders
                              ?.map((fo) => fo.orderGoodsItems ?? [])
                              .flat()
                              ?.filter((ogi) => ogi.article?.id === g.article?.id);

                          const deliveredGoodsQuantity = !deliveredGoods
                              ? 0
                              : deliveredGoods.reduce((acc, val) => {
                                    return (acc += val.quantity ?? 0);
                                }, 0);

                          if (existingGoods.availableQuantity < g.orderedQuantity - deliveredGoodsQuantity) {
                              return true;
                          }

                          return false;
                      }).length === 0
                    : true;

            const alreadyOrderedItems =
                order.orders
                    ?.filter((o) => o.orderStatus !== OrderStatus.Cancelled && o.orderStatus !== OrderStatus.Returned)
                    .map((o) => o.orderGoodsItems ?? [])
                    .flat() ?? [];

            return createData(
                order.id,
                CompleteOrderType.GeneralReturn,
                order.originSite,
                undefined,
                undefined,
                undefined,
                order.returnOrderStatus,
                order.orderGoodsItems,
                sites,
                clients,
                isThereEnoughGoodsForOrder,
                false,
                order.created,
                alreadyOrderedItems,
                undefined,
            );
        });

        const demandOrderRows = demandOrders?.map((order) => {
            const alreadyOrderedItems =
                order.orders
                    ?.filter((o) => o.orderStatus !== OrderStatus.Cancelled && o.orderStatus !== OrderStatus.Returned)
                    .map((o) => o.orderGoodsItems ?? [])
                    .flat() ?? [];

            return createData(
                order.id,
                CompleteOrderType.Demand,
                undefined,
                undefined,
                order.destinationSite,
                undefined,
                order.demandOrderStatus,
                order.orderGoodsItems,
                sites,
                clients,
                true,
                false,
                order.created,
                alreadyOrderedItems,
                undefined,
            );
        });

        return (orderRows ?? [])
            .concat(returnOrderRows ?? [])
            .concat(demandOrderRows ?? [])
            .sort((a, b) => {
                const aConst =
                    a.orderStatus === OrderStatus.Delivered
                        ? 0
                        : a.orderStatus === OrderStatus.Cancelled
                          ? 1
                          : a.orderStatus === OrderStatus.Returned
                            ? 2
                            : 3;
                const bConst =
                    b.orderStatus === OrderStatus.Delivered
                        ? 0
                        : b.orderStatus === OrderStatus.Cancelled
                          ? 1
                          : b.orderStatus === OrderStatus.Returned
                            ? 2
                            : 3;

                if (aConst === bConst) {
                    return CompareTwoDateTimes(b.created ?? new Date(), a.created ?? new Date(), false) ? -1 : 1;
                }
                return bConst - aConst;
            });
    }, [orderRows, returnOrders, demandOrders, goods, sites, clients, approvedOrderGoodsItems]);

    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - (rows?.length ?? 0)) : 0;

    const visibleRows = React.useMemo(
        () => (!rows ? [] : [...rows].slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)),
        [rows, page, rowsPerPage],
    );

    React.useEffect(() => {
        if (!rows || rows?.length <= page * rowsPerPage) {
            setPage(0);
        }
    }, [rows, page]);

    const navigate = useNavigate();

    const handleGoToOrderDetails = (orderId?: number, orderType?: CompleteOrderType) => {
        if (!!orderId && allowGoingToOrderDetails(orderType)) {
            if (orderType === CompleteOrderType.GeneralReturn) {
                navigate('returnOrder/' + orderId.toString());
            } else if (orderType === CompleteOrderType.Demand) {
                navigate('demandOrder/' + orderId.toString());
            }
        }
    };

    const allowGoingToOrderDetails = (orderType?: CompleteOrderType) => {
        return (
            !isHighestPrivilageWarehouseOwner &&
            (orderType === CompleteOrderType.Demand || orderType === CompleteOrderType.GeneralReturn) &&
            (path.indexOf('/demandOrder') === -1 || path.indexOf('/returnOrder') === -1)
        );
    };

    const rowActions: OrderRowActions = {
        goToOrderForm,
        goToAcceptOrderForm,
        goToReceiptForm,
        goToWaybillForm,
        goToCancelOrderForm,
        goToCloseOrderForm,
        goToDemandOrderForm,
        goToReturnOrderForm,
        acceptDemandOrder: handleAcceptDemandOrder,
        acceptReturnOrder: handleAcceptReturnOrder,
        goToPrintWaybill,
        rejectDelivery: setRejectDeliveryOrderId,
        goToReceiptListForOrder,
        goToOrderDetails: handleGoToOrderDetails,
        allowGoingToOrderDetails,
    };

    return (
        <>
            <Box sx={{ width: '100%' }}>
                <Paper sx={{ width: '100%', mb: 2 }}>
                    {rows.length < page * rowsPerPage && rows.length > 0 ? (
                        <div>
                            <CircularProgress size={15} color="inherit" />
                        </div>
                    ) : (
                        <>
                            <TableContainer
                                sx={{
                                    minHeight: '100px',
                                    margin: 'auto',
                                }}
                            >
                                <Table size="small" stickyHeader aria-label="simple table">
                                    <TableHead sx={mobileHiddenHeadSx}>
                                        <TableRow>
                                            <StyledTableCell
                                                sx={{
                                                    display: { xs: 'none', sm: 'table-cell' },
                                                }}
                                            />
                                            <StyledTableCell
                                                sx={{
                                                    display: { xs: 'none', sm: 'table-cell' },
                                                }}
                                            >
                                                Id
                                            </StyledTableCell>
                                            <StyledTableCell align="center">Vrsta naloga</StyledTableCell>
                                            <StyledTableCell
                                                align="center"
                                                sx={{
                                                    display: { xs: 'none', sm: 'table-cell' },
                                                }}
                                            >
                                                Dokumenti
                                            </StyledTableCell>
                                            <StyledTableCell align="center">Odredište</StyledTableCell>
                                            <StyledTableCell align="center">Izvor</StyledTableCell>
                                            <StyledTableCell align="center">Status</StyledTableCell>
                                            <StyledTableCell align="center">Datum kreiranja</StyledTableCell>
                                            {!disableActions && (
                                                <StyledTableCell align="center" sx={{ width: 100 }}>
                                                    Akcije
                                                </StyledTableCell>
                                            )}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {visibleRows?.map((row) => (
                                            <Row
                                                key={`${row.orderType ?? 'order'}-${row.id}`}
                                                row={row}
                                                disableActions={disableActions}
                                                empId={empId}
                                                isWarehouseOwner={isWarehouseOwner}
                                                isProjectManager={isProjectManager}
                                                isWarehouseManager={isWarehouseManager}
                                                actions={rowActions}
                                            />
                                        ))}
                                        {emptyRows > 0 && (
                                            <TableRow
                                                style={{
                                                    height: 53 * emptyRows,
                                                }}
                                            >
                                                <TableCell colSpan={8} />
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                rowsPerPageOptions={[10, 25, 50]}
                                component="div"
                                count={rows?.length ?? 0}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                labelRowsPerPage={''}
                            />
                        </>
                    )}
                </Paper>
            </Box>
            {(!!orderId || !!acceptDemandOrderOrderId || !!acceptReturnOrderOrderId) && (
                <OrderForm
                    closeForm={handleCloseOrderForm}
                    open={!!orderId || !!acceptDemandOrderOrderId || !!acceptReturnOrderOrderId}
                    orderId={orderId}
                    demandOrderId={acceptDemandOrderOrderId}
                    returnOrderId={acceptReturnOrderOrderId}
                    originOrderType={orderType}
                />
            )}
            {!!acceptOrderId && (
                <AcceptOrderForm
                    closeForm={handleCloseAcceptOrderForm}
                    open={!!acceptOrderId}
                    orderId={acceptOrderId}
                />
            )}
            {!!demandOrderId && (
                <DemandOrderForm
                    closeForm={handleCloseDemandOrderForm}
                    open={!!demandOrderId}
                    demandOrderId={demandOrderId}
                />
            )}
            {!!returnOrderId && (
                <ReturnOrderForm
                    closeForm={handleCloseReturnOrderForm}
                    open={!!returnOrderId}
                    returnOrderId={returnOrderId}
                />
            )}
            {!!receiptFormOrderId && (
                <ReceiptForm
                    closeForm={handleCloseReceiptForm}
                    open={!!receiptFormOrderId}
                    orderId={receiptFormOrderId}
                />
            )}
            {!!waybillFormOrderId && (
                <WaybillForm
                    closeForm={handleCloseWaybillForm}
                    open={!!waybillFormOrderId}
                    orderId={waybillFormOrderId}
                />
            )}
            {!!receiptListOrderId && (
                <ReceiptsList
                    closeForm={handleCloseReceiptList}
                    open={!!receiptListOrderId}
                    orderId={receiptListOrderId}
                />
            )}
            {!!cancelOrderOrderId && (
                <CancelOrderForm
                    closeForm={handleCloseCancelOrderForm}
                    open={!!cancelOrderOrderId}
                    orderId={cancelOrderOrderId}
                    orderType={orderType}
                />
            )}
            {!!closeOrderOrderId && (
                <CloseOrderForm
                    closeForm={handleCloseCloseOrderForm}
                    open={!!closeOrderOrderId}
                    orderId={closeOrderOrderId}
                    orderType={orderType}
                />
            )}
            <ResponsiveDialog
                open={!!rejectDeliveryOrderId}
                onClose={() => !rejectingDelivery && setRejectDeliveryOrderId(undefined)}
            >
                <FormDialogTitle accent="error">Nisam primio robu</FormDialogTitle>
                <DialogContent>
                    Nalog će biti označen kao vraćen, a roba će se u sistemu vratiti na izvorno skladište. Ova akcija
                    ostaje zabeležena sa vašim imenom.
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectDeliveryOrderId(undefined)} disabled={rejectingDelivery}>
                        Odustani
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={handleConfirmRejectDelivery}
                        disabled={rejectingDelivery}
                    >
                        {rejectingDelivery ? <CircularProgress size={15} color="inherit" /> : 'Potvrdi'}
                    </Button>
                </DialogActions>
            </ResponsiveDialog>
        </>
    );
}
